import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { PropertyDataSliceExtraReducer, SyncState } from '../types.ts';
import { loadPropertyPayloadToPropertyData } from '../utils/loadPropertyPayloadToPropertyData.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { loadNewComps } from './actions/loadNewComps.ts';
import { loadProperties } from './actions/loadProperties.ts';
import { removeComp, removeComps } from './actions/removeComp.ts';
import { propertyDataAdapter } from './propertyDataAdapter.ts';
import { featureGridConflictedFieldsExtraReducers } from './reducers/extraReducers/featureGridConflictedFieldsExtraReducers.ts';
import { improvementsExtraReducers } from './reducers/extraReducers/improvementsExtraReducers.ts';
import { setOpinionOfSiteValueExtraReducer } from './reducers/extraReducers/setOpinionOfSiteValueExtraReducer.ts';
import { submitEditPropertyFormExtraReducers } from './reducers/extraReducers/submitEditPropertyFormExtraReducers.tsx';
import { setManualAdjustmentsReducer } from './reducers/setManualAdjustmentsReducer.ts';
import { setPropertyDataReducer } from './reducers/setPropertyDataReducer.ts';
import { setSiteValueCommentaryReducer } from './reducers/setSiteValueCommentaryReducer.ts';

export const initialPropertyState = propertyDataAdapter.getInitialState();

const propertyDataSlice = createSlice({
  name: 'propertyData',
  initialState: initialPropertyState,
  reducers: {
    setPropertyData: setPropertyDataReducer,
    setManualAdjustments: setManualAdjustmentsReducer,
    setSiteValueCommentary: setSiteValueCommentaryReducer,
    mutationComplete: (state, action: PayloadAction<SyncState & { id: string }>) => {
      const { id, ...syncState } = action.payload;
      const current = state.entities[id];

      propertyDataAdapter.updateOne(state, {
        id,
        changes: {
          ...current,
          syncState,
        },
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, propertyDataAdapter.removeAll);
    builder.addCase(removeComp, propertyDataAdapter.removeOne);
    builder.addCase(removeComps, (state, action) => {
      const idsToRemove = action.payload.ids;
      propertyDataAdapter.removeMany(state, idsToRemove);
    });
    builder.addCase(loadProperties, (state, action) => {
      propertyDataAdapter.upsertMany(
        state,
        action.payload.properties.map(loadPropertyPayloadToPropertyData),
      );
    });
    builder.addCase(loadNewComps, (state, action) => {
      propertyDataAdapter.upsertMany(
        state,
        action.payload.comps.map(loadPropertyPayloadToPropertyData),
      );
    });

    const extraReducers: PropertyDataSliceExtraReducer[] = [
      improvementsExtraReducers,
      submitEditPropertyFormExtraReducers,
      featureGridConflictedFieldsExtraReducers,
      setOpinionOfSiteValueExtraReducer,
    ];

    extraReducers.forEach((extraReducer) => extraReducer(builder));
  },
});

export const { setPropertyData, setManualAdjustments, setSiteValueCommentary, mutationComplete } =
  propertyDataSlice.actions;

export const propertyDataReducer = propertyDataSlice.reducer;
